// Formularz kryteriów zapisanego filtra — odwzorowanie panelu filtrów z aplikacji klienta
// (`lib/utils/global_widgets/filters` w best_biznes) na tyle wiernie, na ile panel może:
// branże i województwa wybiera się ze słownika, resztę list wpisuje się jako tagi, bo
// słowniki CPV/miast żyją w wyszukiwarce, do której panel nie chodzi.
//
// Komponent jest kontrolowany i operuje na kanonicznym kształcie `SavedFilter`. CZYTA
// pobłażliwie (wiersz z bazy może mieć `"wadium": ["true"]` sprzed normalizacji), PISZE
// kanonicznie — dokładnie tak, jak zapisze to backend.

import { Card, Col, Collapse, DatePicker, Divider, InputNumber, Row, Select, Switch, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import type { FilterDictionaries, SavedFilter } from "../types/filterAdmin";
import { SORT_FIELD_LABELS } from "../types/filterAdmin";

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Odczyt kryterium w każdym kształcie, w jakim mogło zostać zapisane. Bliźniaki funkcji
// `as_*` z `domain::admin::filter_edit` — panel musi otworzyć również stary wiersz.
// ---------------------------------------------------------------------------

function firstOf(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

function readList(filters: Record<string, unknown>, key: string): string[] {
  const value = filters[key];
  const raw = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  const out: string[] = [];
  for (const entry of raw) {
    const text = String(entry ?? "").trim();
    if (text.length > 0 && !out.includes(text)) out.push(text);
  }
  return out;
}

function readBool(filters: Record<string, unknown>, key: string): boolean {
  const value = firstOf(filters[key]);
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return ["true", "1"].includes(value.trim().toLowerCase());
  return false;
}

function readNumber(filters: Record<string, unknown>, key: string): number | null {
  const value = firstOf(filters[key]);
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readDate(filters: Record<string, unknown>, key: string): Dayjs | null {
  const value = firstOf(filters[key]);
  if (typeof value !== "string" || value.trim().length === 0) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

/** Zapis kryterium. Pusta wartość USUWA klucz — wyczyszczone pole nie może zostać w wierszu. */
function withCriterion(
  filter: SavedFilter,
  key: string,
  value: string[] | boolean | number | string | null,
): SavedFilter {
  const filters = { ...filter.filters };
  const empty =
    value === null ||
    value === false ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "string" && value.trim().length === 0);
  if (empty) {
    delete filters[key];
  } else {
    filters[key] = value;
  }
  return { ...filter, filters };
}

type TagFieldProps = {
  label: string;
  filterKey: string;
  value: SavedFilter;
  onChange: (next: SavedFilter) => void;
  placeholder?: string;
  span?: number;
};

function TagField({ label, filterKey, value, onChange, placeholder, span = 8 }: TagFieldProps) {
  return (
    <Col xs={24} md={span}>
      <Text strong>{label}</Text>
      <Select
        mode="tags"
        style={{ width: "100%", marginTop: 4 }}
        tokenSeparators={[",", ";", "\n"]}
        placeholder={placeholder}
        value={readList(value.filters, filterKey)}
        onChange={(values: string[]) => onChange(withCriterion(value, filterKey, values))}
      />
    </Col>
  );
}

type DateFieldProps = {
  label: string;
  filterKey: string;
  value: SavedFilter;
  onChange: (next: SavedFilter) => void;
};

function DateField({ label, filterKey, value, onChange }: DateFieldProps) {
  return (
    <Col xs={24} md={6}>
      <Text strong>{label}</Text>
      <DatePicker
        showTime
        style={{ width: "100%", marginTop: 4 }}
        value={readDate(value.filters, filterKey)}
        // ISO-8601 w UTC — ten sam format, który zapisuje aplikacja klienta.
        onChange={(date) => onChange(withCriterion(value, filterKey, date ? date.toISOString() : null))}
      />
    </Col>
  );
}

type ToggleProps = {
  label: string;
  filterKey: string;
  value: SavedFilter;
  onChange: (next: SavedFilter) => void;
};

function Toggle({ label, filterKey, value, onChange }: ToggleProps) {
  return (
    <Col xs={12} md={6}>
      <Switch
        checked={readBool(value.filters, filterKey)}
        onChange={(checked) => onChange(withCriterion(value, filterKey, checked))}
      />{" "}
      <Text>{label}</Text>
    </Col>
  );
}

export function SavedFilterForm({
  value,
  onChange,
  dictionaries,
}: {
  value: SavedFilter;
  onChange: (next: SavedFilter) => void;
  dictionaries: FilterDictionaries | null;
}) {
  const phraseCount = value.queries.length;
  const maxPhrases = dictionaries?.max_search_phrases ?? 20;

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text strong>Frazy wyszukiwania</Text>
          <Select
            mode="tags"
            style={{ width: "100%", marginTop: 4 }}
            tokenSeparators={["\n"]}
            placeholder="np. koparka"
            value={value.queries}
            status={phraseCount > maxPhrases ? "error" : undefined}
            onChange={(queries: string[]) => {
              const cleaned = queries.map((q) => q.trim()).filter((q) => q.length > 0 && q !== "*");
              // `search_text` to sklejona reprezentacja tych samych fraz — trzymamy je zgodne,
              // żeby czytelnik sięgający po starsze pole widział to samo.
              onChange({ ...value, queries: cleaned, search_text: cleaned.join("\n") });
            }}
          />
          <Text type="secondary">
            Ogłoszenie pasuje, gdy trafi w DOWOLNĄ frazę (LUB). Limit wyszukiwarki: {maxPhrases}
            {phraseCount > 0 ? ` — teraz ${phraseCount}` : ""}.
          </Text>
        </Col>
      </Row>

      <Text strong>Wyklucz frazy</Text>
      <Select
        mode="tags"
        aria-label="Wyklucz frazy"
        style={{ width: "100%", marginTop: 4 }}
        value={value.excluded_phrases ?? []}
        maxCount={20}
        tokenSeparators={["\n"]}
        onChange={(items: string[]) => onChange({ ...value, excluded_phrases:
          items.map((p) => p.trim().replace(/\s+/g, " ")).filter(Boolean) })}
      />
      <Text type="secondary">Kolejne słowa bez synonimów. Maksymalnie 20 fraz po 200 znaków.</Text>
      <Divider titlePlacement="start" plain>
        Przedmiot zamówienia
      </Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Text strong>Branże</Text>
          <Select
            mode="multiple"
            style={{ width: "100%", marginTop: 4 }}
            placeholder="Wybierz branże"
            optionFilterProp="label"
            value={readList(value.filters, "industries")}
            options={(dictionaries?.industries ?? []).map((industry) => ({
              value: industry.slug,
              label: industry.name,
            }))}
            onChange={(values: string[]) => onChange(withCriterion(value, "industries", values))}
          />
        </Col>
        <TagField
          label="Kody CPV"
          filterKey="cpv_code"
          value={value}
          onChange={onChange}
          placeholder="np. 45000000-7 (kod obejmuje całe poddrzewo)"
          span={12}
        />
      </Row>

      <Divider titlePlacement="start" plain>
        Lokalizacja
      </Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Text strong>Województwa</Text>
          <Select
            mode="multiple"
            style={{ width: "100%", marginTop: 4 }}
            placeholder="Wybierz województwa"
            optionFilterProp="label"
            value={readList(value.filters, "states")}
            options={(dictionaries?.voivodeships ?? []).map((state) => ({ value: state, label: state }))}
            onChange={(values: string[]) => onChange(withCriterion(value, "states", values))}
          />
        </Col>
        <TagField label="Powiaty" filterKey="districts" value={value} onChange={onChange} />
        <TagField label="Miasta" filterKey="cities" value={value} onChange={onChange} />
        <TagField label="Kraje" filterKey="country" value={value} onChange={onChange} />
      </Row>

      <Divider titlePlacement="start" plain>
        Zamawiający i klasyfikacja
      </Divider>
      <Row gutter={[16, 16]}>
        <TagField label="Organizatorzy" filterKey="organizer_name" value={value} onChange={onChange} />
        <TagField label="Źródła" filterKey="source" value={value} onChange={onChange} />
        <TagField label="Kategorie" filterKey="category" value={value} onChange={onChange} />
        <TagField label="Formy" filterKey="form" value={value} onChange={onChange} />
      </Row>

      <Divider titlePlacement="start" plain>
        Budżet i daty
      </Divider>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Text strong>Budżet od</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 4 }}
            min={0}
            value={readNumber(value.filters, "budget_min")}
            onChange={(next) => onChange(withCriterion(value, "budget_min", next ?? null))}
          />
        </Col>
        <Col xs={12} md={6}>
          <Text strong>Budżet do</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 4 }}
            min={0}
            value={readNumber(value.filters, "budget_max")}
            onChange={(next) => onChange(withCriterion(value, "budget_max", next ?? null))}
          />
        </Col>
        <DateField label="Dodano od" filterKey="created_at_min" value={value} onChange={onChange} />
        <DateField label="Dodano do" filterKey="created_at_max" value={value} onChange={onChange} />
        <DateField label="Składanie od" filterKey="submitting_date_min" value={value} onChange={onChange} />
        <DateField label="Składanie do" filterKey="submitting_date_max" value={value} onChange={onChange} />
      </Row>

      <Divider titlePlacement="start" plain>
        Przełączniki
      </Divider>
      <Row gutter={[16, 16]}>
        <Toggle label="Tylko z wadium" filterKey="wadium" value={value} onChange={onChange} />
        <Toggle label="Tylko z załącznikami" filterKey="attachments" value={value} onChange={onChange} />
        <Toggle label="Tylko z linkiem źródłowym" filterKey="original_link" value={value} onChange={onChange} />
        <Toggle label="Ukryj nieprzetłumaczone" filterKey="hide_untranslated" value={value} onChange={onChange} />
      </Row>
      <Text type="secondary">
        Zdjęty przełącznik usuwa kryterium. Nie oznacza „pokaż wyłącznie ogłoszenia bez wadium”.
      </Text>

      <Collapse
        style={{ marginTop: 16 }}
        items={[
          {
            key: "advanced",
            label: "Kryteria rzadkie i legacy",
            children: (
              <Row gutter={[16, 16]}>
                <TagField
                  label="Branże (stara taksonomia)"
                  filterKey="branches"
                  value={value}
                  onChange={onChange}
                  placeholder="sprzed migracji CPV — filtruje po data.branches"
                />
                <TagField label="Typ ogłoszenia" filterKey="auction_type" value={value} onChange={onChange} />
                <TagField label="Status (lista klienta)" filterKey="status" value={value} onChange={onChange} />
                <TagField label="E-maile kontaktowe" filterKey="emails" value={value} onChange={onChange} />
                <TagField label="Telefony kontaktowe" filterKey="phones" value={value} onChange={onChange} />
                <TagField label="ID ogłoszeń" filterKey="id" value={value} onChange={onChange} />
              </Row>
            ),
          },
        ]}
      />

      <Card size="small" title="Sortowanie i rozmiar listy" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={10}>
            <Text strong>Sortowanie</Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={value.sort}
              options={(dictionaries?.sort_fields ?? ["_score"]).map((field) => ({
                value: field,
                label: SORT_FIELD_LABELS[field] ?? field,
              }))}
              onChange={(sort: string) => onChange({ ...value, sort })}
            />
          </Col>
          <Col xs={12} md={7}>
            <Text strong>Kierunek</Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={value.direction}
              options={[
                { value: "desc", label: "Malejąco" },
                { value: "asc", label: "Rosnąco" },
              ]}
              onChange={(direction: string) => onChange({ ...value, direction })}
            />
          </Col>
          <Col xs={12} md={7}>
            <Text strong>Liczba wyników</Text>
            <InputNumber
              style={{ width: "100%", marginTop: 4 }}
              min={1}
              max={dictionaries?.max_filter_limit ?? 100}
              value={value.limit}
              onChange={(limit) => onChange({ ...value, limit: limit ?? 25 })}
            />
          </Col>
        </Row>
      </Card>
    </>
  );
}
